import FabricAnalyticsListeners, { FabricChannel } from '@atlaskit/analytics-listeners';
import { Notifications } from '@atlaskit/atlassian-navigation';
import Popup from '@atlaskit/popup';
import { LoggerContextProvider } from '@atlassian/notification-list';
import { Severity } from '@sentry/browser';
import { inject, observer } from 'mobx-react';
import React, { Suspense } from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';

import { FeatureFlagEnum } from '../../../../clients/FeatureFlagsClient';
import { performance } from '../../../../facade/window';
import { AnalyticsConsumerProps, withAnalytics } from '../../../../hoc/withAnalytics';
import { useFabricAnalyticsListenerAdapter } from '../../../../hooks/useFabricAnalyticsListener';
import { addBreadcrumb, BreadcrumbCategory, captureException, captureMessage } from '../../../../sentry';
import Store from '../../../../store';
import FeatureFlagsStore from '../../../../store/featureFlagsStore';
import { useStore } from '../../../../store/index';
import NotificationsStore from '../../../../store/notificationsStore';
import UserStore from '../../../../store/userStore';
import { notificationMessages as messages } from '../../../common/intl/notifications';
import ErrorBoundary from '../../../ErrorBoundary/ErrorBoundary';

import NotificationsBadge from './NotificationsBadge';

const AsyncNotificationList = React.lazy(() =>
  import(
    /* webpackChunkName: "notification-list-deferred" */
    '@atlassian/notification-list'
  ),
);

const NotificationListContainer = styled.div`
  height: calc(100vh - 100px);
  width: 540px;
  max-width: 100vw;
`;

const logger = {
  captureException: (error: Error, tags?: Record<string, string>): void => {
    captureException(error, tags);
  },
  captureMessage: (message: string, tags?: Record<string, string>): void => {
    captureMessage(message, tags);
  },
};

interface NotificationListContentProps {
  userId: string | undefined;
}

const isFeatureEnabled = (featureFlagsStore: FeatureFlagsStore, key: FeatureFlagEnum): boolean =>
  Boolean(featureFlagsStore.isDone && featureFlagsStore.flags[key]) || false;

const NotificationListContent = observer((props: NotificationListContentProps) => {
  const fabricAnalyticsListener = useFabricAnalyticsListenerAdapter();
  const featureFlagsStore = useStore('featureFlagsStore');
  if (!featureFlagsStore.isDone) {
    return null;
  }
  const isGroupingEnabled = isFeatureEnabled(featureFlagsStore, 'uchi.enable-notifications-grouping');
  return (
    <ErrorBoundary subject="notificationComponent">
      <NotificationListContainer>
        <Suspense fallback={<div />}>
          <FabricAnalyticsListeners
            client={fabricAnalyticsListener}
            excludedChannels={[
              FabricChannel.atlaskit,
              FabricChannel.elements,
              FabricChannel.editor,
              FabricChannel.media,
            ]}
          >
            <LoggerContextProvider logger={logger}>
              <AsyncNotificationList
                testId="uchi-notifications-component"
                userId={props.userId}
                isGlobalStoreEnabled={true}
                featureFlags={{
                  enableGrouping: isGroupingEnabled,
                }}
              />
            </LoggerContextProvider>
          </FabricAnalyticsListeners>
        </Suspense>
      </NotificationListContainer>
    </ErrorBoundary>
  );
});

interface Props {
  notificationsStore: NotificationsStore;
  userStore: UserStore;
}

type ComponentProps = Props & InjectedIntlProps & AnalyticsConsumerProps & RouteComponentProps;

interface State {
  isOpen: boolean;
}

@observer
class NavNotificationsButton extends React.Component<ComponentProps, State> {
  state: State = {
    isOpen: false,
  };

  render() {
    const { isOpen } = this.state;
    const { notificationsStore, intl, userStore } = this.props;
    const tooltipText = [
      `${intl.formatMessage(messages.notifications)}`,
      notificationsStore.unseenCount > 0 ? ` (${notificationsStore.unseenCount})` : '',
    ].join('');

    return (
      <Popup
        placement="bottom-end"
        content={() => <NotificationListContent userId={userStore.accountId} />}
        isOpen={isOpen}
        id={'nav__notification'}
        testId="nav__notification-menu"
        onClose={() => this.setIsOpen(false)}
        trigger={triggerProps => (
          <Notifications
            badge={NotificationsBadge}
            testId="nav__notification-menu-trigger"
            onClick={() => {
              notificationsStore.acknowledgeUnseenNotificationsCount();
              this.setIsOpen(!isOpen);
            }}
            tooltip={tooltipText}
            isSelected={isOpen}
            {...triggerProps}
          />
        )}
      />
    );
  }

  setIsOpen = (open: boolean) => {
    addBreadcrumb({
      category: BreadcrumbCategory.UiClick,
      level: Severity.Info,
      message: open ? 'notificationDrawer opened' : 'notificationDrawer closed',
    });
    this.setState({ isOpen: open });

    if (open) {
      performance().mark('notification.list.render.start');
    } else {
      performance().clearMarks('notification.list.render.start');
    }
  };
}

export default inject(({ notificationsStore, userStore }: Store) => ({
  notificationsStore,
  userStore,
  // @ts-ignore - withRouter prop typings are different to the other HOC default prop typings
}))(withRouter(injectIntl(withAnalytics(NavNotificationsButton))));
